// clientStater.js
import { ResponseMsg } from './protobuf/response';
import { Package } from "./protobuf/msgPackage";
import { AckMsg } from './protobuf/ack';
import { PullResponseMsg } from './protobuf/pullResponse';
import { HeartbeatMsg } from './protobuf/heartbeat';
import { MsgReadStatusMsg } from './protobuf/msgReadStatus';
import { CallEventMsg } from './protobuf/callEvent';
import { CallEventOptMsg } from './protobuf/callEventOpt';
import { ByteUtil } from "./utils/index";
import { ResponsePasrser, MsgParser, PullReaponseParser, HearbeatParser } from './domain/index';
import { EventBus } from './domain/eventBus/eventBus';
import CallParser from './domain/msg/callParser';
import MsgReadStatusParser from './domain/msg/msgReadStatusParser';

export default class ClientStater {
    constructor(url, opts, events) {
        this.ws = new WebSocket(url);
        this.opts = opts;
        this.events = events;

        // 添加新消息回调
        this.onNewMessageCallback = null;
        // 添加普通消息回调
        this.onMessageCallback = null;
        // 添加消息回调队列，确保顺序处理
        this.messageQueue = [];
        this.isProcessingQueue = false;

        this.initListeners();

        EventBus.initBus();

        // 心跳相关属性
        this.heartbeatTimer = null;
        this.heartbeatInterval = 30000;
        this.lastHeartbeatTime = Date.now();
    }

    // 设置新消息回调（用于长轮询）
    setOnNewMessageCallback(callback) {
        this.onNewMessageCallback = callback;
    }

    // 设置普通消息回调
    setOnMessageCallback(callback) {
        this.onMessageCallback = callback;
    }

    // 处理消息队列
    async processMessageQueue() {
        if (this.isProcessingQueue || this.messageQueue.length === 0) return;
        this.isProcessingQueue = true;

        while (this.messageQueue.length > 0) {
            const data = this.messageQueue.shift();
            try {
                await this.handleMessage(data);
            } catch (e) {
                console.error('[im] 处理消息错误:', e);
            }
        }

        this.isProcessingQueue = false;
    }

    // 处理单条消息
    async handleMessage(event) {
        var data = event?.data || [];

        if (data.length <= 8) {
            return;
        }

        var len = ByteUtil.dataViewToInt(data, 0);
        var dataType = ByteUtil.dataViewToInt(data, 4);
        var sourceArr = (new Uint8Array(data)).slice(8);

        if (len != sourceArr.length) {
            return;
        }

        // 重置心跳监控
        this.resetHeartbeatTimer();

        switch (dataType) {
            case 0: { // ACK状态消息
                let ackContent = AckMsg.decode(new Uint8Array(sourceArr));
                MsgParser.processAck(this, ackContent);
                break;
            }
            case 1: { // 心跳消息
                let hearbeat = HeartbeatMsg.decode(new Uint8Array(sourceArr));
                HearbeatParser.process(this, hearbeat);
                this.startHeartbeatMonitor(30000);
                break;
            }
            case 5: { // 响应 response
                let responseMsg = ResponseMsg.decode(new Uint8Array(sourceArr));
                ResponsePasrser.process(this, responseMsg);
                break;
            }
            case 6: { // 数组聊天消息
                let msgPack = Package.decode(new Uint8Array(sourceArr));
                if (msgPack.msg && msgPack.msg.length > 0 && this.onMessageCallback) {
                    // 直接调用回调，让上层处理
                    this.onMessageCallback({
                        type: 'message',
                        messages: msgPack.msg,
                        timestamp: Date.now()
                    });
                }
                break;
            }
            case 10: { // 拉取的聊天消息
                let pullResponse = PullResponseMsg.decode(new Uint8Array(sourceArr));
                PullReaponseParser.process(this, pullResponse);

                if (pullResponse.msg && pullResponse.msg.length > 0 && this.onMessageCallback) {
                    this.onMessageCallback({
                        type: 'polling',
                        fromGuid: pullResponse.fromGuid,
                        messages: pullResponse.msg,
                        hasMore: pullResponse.hasMore
                    });
                }
                break;
            }
            case 21: {
                let callEvent = CallEventMsg.decode(new Uint8Array(sourceArr));
                CallParser.process(this, callEvent);
                break;
            }
            case 22: {
                let callEventOpt = CallEventOptMsg.decode(new Uint8Array(sourceArr));
                CallParser.processOpt(this, callEventOpt);
                break;
            }
            case 24: {
                let readStatusMsg = MsgReadStatusMsg.decode(new Uint8Array(sourceArr));
                MsgReadStatusParser.processReadStatusMsg(this, readStatusMsg);
                break;
            }
        }
    }

    initListeners() {
        this.ws.onopen = () => {
            console.log('[im] 链接打开');
            if (this.opts.onconnect) {
                this.opts.onconnect();
            }
            this.ws.binaryType = 'arraybuffer';
            this.resetHeartbeatTimer();
            this.startHeartbeatMonitor();
        };

        this.ws.onmessage = (event) => {
            // 将消息加入队列顺序处理
            this.messageQueue.push(event);
            this.processMessageQueue();
        };

        this.ws.onclose = (_event) => {
            console.log('[im] 链接断开');
            EventBus.clear();
            if (this.opts.ondisconnect) {
                this.events.disconnectEvent();
                this.opts.ondisconnect();
            }
        };

        this.ws.onerror = (error) => {
            console.error('[im] WebSocket 错误:', error);
        };
    }

    // 添加检查连接状态的方法
    isConnected() {
        return this.ws && this.ws.readyState === WebSocket.OPEN;
    }

    // 添加获取连接状态的方法
    getConnectionState() {
        if (!this.ws) return 'CLOSED';

        switch (this.ws.readyState) {
            case WebSocket.CONNECTING:
                return 'CONNECTING';
            case WebSocket.OPEN:
                return 'OPEN';
            case WebSocket.CLOSING:
                return 'CLOSING';
            case WebSocket.CLOSED:
                return 'CLOSED';
            default:
                return 'UNKNOWN';
        }
    }

    startHeartbeatMonitor(interval = null) {
        if (this.heartbeatTimer) {
            clearInterval(this.heartbeatTimer);
        }

        const checkInterval = interval || this.heartbeatInterval;

        this.heartbeatTimer = setInterval(() => {
            const now = Date.now();
            const timeSinceLastHeartbeat = now - this.lastHeartbeatTime;

            // 如果超过60秒没有收到心跳，认为连接断开
            if (timeSinceLastHeartbeat > 60000) {
                console.log('[im] 心跳超时，准备断开连接');
                this.handleHeartbeatTimeout();
            }
        }, checkInterval);
    }

    resetHeartbeatTimer() {
        this.lastHeartbeatTime = Date.now();
    }

    handleHeartbeatTimeout() {
        if (this.heartbeatTimer) {
            clearInterval(this.heartbeatTimer);
            this.heartbeatTimer = null;
        }

        // 检查实际连接状态
        if (!this.isConnected()) {
            console.log('[im] 连接已断开，触发重连');
            if (this.opts.heartbeatdisconnect) {
                this.opts.heartbeatdisconnect();
            }
        } else {
            console.log('[im] 连接仍在，重置心跳时间');
            this.resetHeartbeatTimer();
        }
    }

    disconnect() {
        console.log('[im] 心跳未收到，im断开');
        EventBus.clear();
        this.events.disconnectEvent();
        if (this.opts.heartbeatdisconnect) {
            this.opts.heartbeatdisconnect();
        }
    }

    onevent(id, callback, opts) {
        EventBus.on(id, (res) => { callback(res); }, opts);
    }

    dispatch(id, data) {
        EventBus.notify(id, data);
        EventBus.remove(id);
    }

    send(content) {
        const arrayBuffer = content.buffer;
        this.ws.send(arrayBuffer);
    }

    close() {
        this.events.disconnectEvent();
        this.ws.close();
    }
}